import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Cpu, Layers, Shield, ArrowRight, Code, GitBranch, Activity, Grid, Clock, Folder, Zap, Database, FileText } from 'react-feather';
import AuthModal from 'shared/components/Auth/AuthModal';
import UserProfileDropdown from 'shared/components/Auth/UserProfileDropdown';
import Footer from 'shared/components/footer';
import { auth } from 'shared/utils/firebase';
import { signOut } from 'firebase/auth';
import { buildMainUrl } from 'shared/utils/navigation';
import styles from './LandingPage.module.css';

const LandingPage = ({ isAuthenticated, userDetails, theme, toggleTheme }) => {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showLearnModal, setShowLearnModal] = useState(false);
  const handleProceedLearn = () => {
    window.open(buildMainUrl('/learning'), '_blank');
    setShowLearnModal(false);
  };

  const openAuth = (mode = 'login') => {
    setAuthMode(mode);
    setShowAuth(true);
  };

  const handleOpenWorkspace = () => {
    navigate('/ide');
  };

  const handleAuthSuccess = () => {
    setShowAuth(false);
    navigate('/ide');
  };

  return (
    <div className={`${styles.landingContainer} ${theme === 'light' ? styles.lightTheme : ''}`}>
      {/* Brand Header */}
      <header className={styles.header}>
        <div className={styles.logoContainer} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} title="Home">
          <img src="/static_img/weblogo.png" alt="SiliconLogix Logo" className={styles.webLogo} />
        </div>

        {/* Navigation Links */}
        <nav className={styles.navLinks}>
          <button className={`${styles.navLink} ${styles.navLinkActive}`} onClick={handleOpenWorkspace}>
            Codebox IDE
          </button>
          {/* <button className={styles.navLink} onClick={handleScrollToFeatures}>
            Features
          </button> */}
          <button className={styles.navLink} onClick={() => navigate('/exercise')}>
            Exercises
          </button>
          <button className={styles.navLink} onClick={() => navigate('/challenges')}>
            Challenges
          </button>
          <button className={styles.navLink} onClick={() => navigate('/examples')}>
            Examples
          </button>
          <button className={styles.navLink} onClick={() => setShowLearnModal(true)}>
            Learn
          </button>
        </nav>

        {/* Right side: Theme Toggle + Auth Buttons OR User Avatar */}
        <div className={styles.navActions}>
          {/* Theme Toggle */}
          <button
            className={styles.themeToggleBtn}
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to Light mode' : 'Switch to Dark mode'}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              /* Sun icon */
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              /* Moon icon */
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>

          {isAuthenticated ? (
            <UserProfileDropdown userDetails={userDetails} styles={styles} hasArrow={true} />
          ) : (
            <>
              <button className={styles.btnNavSignIn} onClick={() => openAuth('login')}>Sign In</button>
              <button className={styles.btnNavSignUp} onClick={() => openAuth('signup')}>Sign Up</button>
            </>
          )}
        </div>
      </header>

      {/* Background glow orbs */}
      <div className={`${styles.glowOrb} ${styles.orb1}`}></div>
      <div className={`${styles.glowOrb} ${styles.orb2}`}></div>

      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <div className={styles.heroBadge}>
            <span className={styles.badgeDot}></span>
            Open-source EDA Tools, Zero Setup
          </div>
          
          <h1 className={styles.heroTitle}>
            Browser-Based Hardware Lab<br />
            <span className={styles.gradientText}>CODEBOX</span>
          </h1>
          
          <p className={styles.heroDesc}>
            Write Verilog, simulate design netlists, inspect logic outputs using VCD waveforms, 
            and synthesize circuits with Yosys. Access full industrial synthesis pipelines instantly 
            without installing any local compiler.
          </p>

          <div className={styles.heroActions}>
            <button className={styles.btnPrimary} onClick={handleOpenWorkspace}>
              Open Workspace <ArrowRight size={18} />
            </button>
            {!isAuthenticated && (
              <button className={styles.btnSecondary} onClick={() => openAuth('signup')}>
                Create Free Account
              </button>
            )}
          </div>

          <div className={styles.heroProof}>
            <span className={styles.proofItem}>✓ Zero installation required</span>
            <span className={styles.proofItem}>✓ Save projects in real-time</span>
            <span className={styles.proofItem}>✓ Icarus, Verilator &amp; Yosys pipelines</span>
          </div>

          {/* IDE Mockup */}
          <div className={styles.mockupContainer} onClick={handleOpenWorkspace}>
            <div className={styles.mockupHeader}>
              <div className={styles.mockupDots}>
                <span className={styles.dotRed}></span>
                <span className={styles.dotYellow}></span>
                <span className={styles.dotGreen}></span>
              </div>
              <div className={styles.mockupTitle}>design1.v &mdash; CodeBox Workspace</div>
            </div>
            <div className={styles.mockupBody}>
              <div className={styles.mockupSidebar}>
                <div className={styles.sidebarSection}>FILES</div>
                <div className={styles.sidebarFile}>📂 src</div>
                <div className={`${styles.sidebarFile} ${styles.activeFile}`}>📄 design1.v</div>
                <div className={styles.sidebarFile}>📄 tb_1.v</div>
                <div className={styles.sidebarFile}>📄 constraints.sdc</div>
              </div>
              <div className={styles.mockupEditor}>
                <pre>
{`module fulladd (  input  a,
                  input  b,
                  input c_in,
                  output reg c_out,
                  output reg sum);

\talways @ (a or b or c_in) begin
\t  {c_out, sum} = a + b + c_in;
\tend
endmodule`}
                </pre>
              </div>
            </div>
            <div className={styles.mockupOverlay}>
              <button className={styles.mockupPlayBtn}>
                <Play size={20} fill="#fff" /> Open Interactive Workspace
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Powered By Strip */}
      <div className={styles.toolsStrip}>
        <div className={styles.container}>
          <div className={styles.toolsLabel}>POWERED BY OPEN-SOURCE EDA TOOLS</div>
          <div className={styles.toolsRow}>
            <span className={styles.toolChip}><Activity size={14} /> Icarus Verilog</span>
            <span className={styles.toolChip}><Zap size={14} /> Verilator</span>
            <span className={styles.toolChip}><Cpu size={14} /> Yosys Synthesis</span>
            <span className={styles.toolChip}><Clock size={14} /> OpenSTA</span>
            <span className={styles.toolChip}><GitBranch size={14} /> OpenROAD</span>
            {/* <span className={styles.toolChip}><Grid size={14} /> KLayout</span> */}
            <span className={styles.toolChip}><Database size={14} /> SkyWater 130nm</span>
            <span className={styles.toolChip}><FileText size={14} /> Coverage</span>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <section className={styles.featuresSection} id="features">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTag}>EVERYTHING YOU NEED</span>
            <h2>The full RTL-to-GDSII flow,<br />
              <span className={styles.gradientText}>built for learning</span>
            </h2>
            <p>Simulation, synthesis, place &amp; route, timing analysis, formal verification &mdash; the same flow used in industry, zero setup required.</p>
          </div>

          <div className={styles.featuresGrid}>
            {/* 1. RTL Simulation */}
            <div className={styles.featureCard}>
              <div className={`${styles.featIcon} ${styles.iconBlue}`}>
                <Play size={20} />
              </div>
              <h3>RTL Simulation</h3>
              <p>Run testbenches with Icarus Verilog or Verilator. Catch bugs instantly with detailed output and signal tracing.</p>
              <div className={styles.cardPills}>
                <span>Icarus Verilog</span>
                <span>Verilator</span>
                <span>VCD output</span>
              </div>
            </div>

            {/* 2. Interactive Waveforms */}
            <div className={styles.featureCard}>
              <div className={`${styles.featIcon} ${styles.iconTeal}`}>
                <Activity size={20} />
              </div>
              <h3>Interactive Waveforms</h3>
              <p>Inspect every signal with the integrated Surfer waveform viewer. Zoom, pan, and measure &mdash; right in the browser.</p>
              <div className={styles.cardPills}>
                <span>Surfer viewer</span>
                <span>VCD / FST</span>
                <span>Multi-signal</span>
              </div>
            </div>

            {/* 3. Logic Synthesis */}
            <div className={styles.featureCard}>
              <div className={`${styles.featIcon} ${styles.iconPurple}`}>
                <Cpu size={20} />
              </div>
              <h3>Logic Synthesis</h3>
              <p>Synthesize to SkyWater 130nm standard cells with Yosys. Get a gate-level netlist, interactive schematic, and area report instantly.</p>
              <div className={styles.cardPills}>
                <span>Yosys</span>
                <span>sky138 PDK</span>
                <span>Schematic</span>
              </div>
            </div>

            {/* 4. Static Timing Analysis */}
            <div className={styles.featureCard}>
              <div className={`${styles.featIcon} ${styles.iconOrange}`}>
                <Clock size={20} />
              </div>
              <h3>Static Timing Analysis</h3>
              <p>Learn static timing with OpenSTA across typical, fast, and slow corners. Critical paths are highlighted in the schematic to help you understand timing.</p>
              <div className={styles.cardPills}>
                <span>OpenSTA</span>
                <span>PPA report</span>
                <span>3 corners</span>
              </div>
            </div>

            {/* 5. Multi-file Projects */}
            <div className={styles.featureCard}>
              <div className={`${styles.featIcon} ${styles.iconBlue}`}>
                <Folder size={20} />
              </div>
              <h3>Multi-file Projects</h3>
              <p>Organize your Verilog designs across multiple files. File explorer, Monaco editor, and one-click simulation of your whole design.</p>
              <div className={styles.cardPills}>
                <span>File explorer</span>
                <span>Monaco editor</span>
                <span>30 projects free</span>
              </div>
            </div>

            {/* 6. Place & Route */}
            <div className={styles.featureCard}>
              <div className={`${styles.featIcon} ${styles.iconGreen}`}>
                <Layers size={20} />
              </div>
              <h3>Place &amp; Route</h3>
              <p>Take your synthesized netlist all the way to a placed-and-routed layout with OpenROAD on SkyWater 130nm. Post-route STA catches timing violations before tape-out.</p>
              <div className={styles.cardPills}>
                <span>OpenROAD</span>
                <span>Post-route STA</span>
                <span>GDSII export</span>
              </div>
            </div>

            {/* 7. Formal & Coverage */}
            <div className={styles.featureCard}>
              <div className={`${styles.featIcon} ${styles.iconPurple}`}>
                <Shield size={20} />
              </div>
              <h3>Formal &amp; Coverage</h3>
              <p>Go beyond simulation with SymbiYosys formal property checking and Covered code coverage analysis. Prove your design correct, not just test it.</p>
              <div className={styles.cardPills}>
                <span>SymbiYosys</span>
                <span>Code coverage</span>
                <span>UVM</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className={styles.workflowSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTag}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <GitBranch size={12} /> HOW IT WORKS
              </span>
            </span>
            <h2>From RTL to GDSII<br />
              <span className={styles.gradientText}>in your browser</span>
            </h2>
          </div>

          <div className={styles.workflowGrid}>
            {/* Step 1 */}
            <div className={styles.workflowStep}>
              <div className={styles.stepNum}>01</div>
              <div className={`${styles.stepIcon} ${styles.stepIconBlue}`}>
                <Code size={18} />
              </div>
              <h4>Code your Design</h4>
              <p>Monaco editor with syntax highlighting, multi-file support, and instant lint feedback. Start from 120+ ready-to-run examples.</p>
            </div>

            {/* Step 2 */}
            <div className={styles.workflowStep}>
              <div className={styles.stepNum}>02</div>
              <div className={`${styles.stepIcon} ${styles.stepIconTeal}`}>
                <Activity size={18} />
              </div>
              <h4>Simulate &amp; Verify</h4>
              <p>Run testbenches with Icarus or Verilator. Inspect signals in the waveform viewer. Add UVM, coverage, or formal verification for deeper confidence.</p>
            </div>

            {/* Step 3 */}
            <div className={styles.workflowStep}>
              <div className={styles.stepNum}>03</div>
              <div className={`${styles.stepIcon} ${styles.stepIconPurple}`}>
                <Cpu size={18} />
              </div>
              <h4>Synthesize &amp; Timing</h4>
              <p>Push through Yosys on SkyWater 130nm. Get a gate-level schematic, PPA report, and OpenSTA timing analysis across all three PVT corners.</p>
            </div>

            {/* Step 4 */}
            <div className={styles.workflowStep}>
              <div className={styles.stepNum}>04</div>
              <div className={`${styles.stepIcon} ${styles.stepIconGreen}`}>
                <Grid size={18} />
              </div>
              <h4>Place, route &amp; tape out</h4>
              <p>Run OpenROAD place &amp; route directly from synthesis. Inspect the final layout with KLayout, run DRC &amp; LVS, and export a real GDSII file.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Learn by Doing Section ─── */}
      <section className={styles.learnSection} id="challenges">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTag}>LEARN BY DOING</span>
            <h2>Three ways to build your<br />
              <span className={styles.gradientText}>design &amp; verification skills</span>
            </h2>
            <p>Guided exercises, graded challenges, and 120+ ready-to-run examples — all wired directly into the IDE.</p>
          </div>

          <div className={styles.learnGrid}>
            {/* Card 1 — Guided Exercises */}
            <div className={styles.learnCard}>
              <div className={`${styles.learnIcon} ${styles.learnIconGreen}`}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                </svg>
              </div>
              <h3>Code Exercises</h3>
              <p>Step-by-step Verilog exercises from combinational basics to pipelined datapaths. Write, simulate, and check your solution in one place.</p>
              <button className={styles.learnLink} onClick={() => navigate('/exercise')}>
                Browse exercises →
              </button>
            </div>

            {/* Card 2 — Design Challenges */}
            <div className={styles.learnCard}>
              <div className={`${styles.learnIcon} ${styles.learnIconYellow}`}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                  <path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
                  <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
                  <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                </svg>
              </div>
              <h3>Design Challenges</h3>
              <p>Design problems graded against hidden testbenches. Get instant feedback on correctness, area, and timing — easy through hard difficulty.</p>
              <button className={styles.learnLink} onClick={() => navigate('/challenges')}>
                View challenges →
              </button>
            </div>

            {/* Card 3 — Reference Examples */}
            <div className={styles.learnCard} id="examples">
              <div className={`${styles.learnIcon} ${styles.learnIconPurple}`}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>
                </svg>
              </div>
              <h3>Reference Examples</h3>
              <p>120+ ready-to-run examples covering FIFOs, state machines, AXI interfaces, and more. Open any example in the IDE and start experimenting.</p>
              <button className={styles.learnLink} onClick={() => navigate('/examples')}>
                Explore examples →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaCard}>
            <h2>Ready to design next-gen chips?</h2>
            <p>Create a free account to unlock workspaces saves, unlimited compilations, and more.</p>
            <div className={styles.ctaButtons}>
              {isAuthenticated ? (
                <button className={styles.btnPrimary} onClick={handleOpenWorkspace}>
                  Open Your Workspace <ArrowRight size={18} />
                </button>
              ) : (
                <>
                  <button className={styles.btnPrimary} onClick={() => openAuth('signup')}>
                    Sign Up Now
                  </button>
                  <button className={styles.btnGhost} onClick={handleOpenWorkspace}>
                    Try Editor as Guest
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      {/* Auth Modal */}
      <AuthModal 
        show={showAuth} 
        onClose={() => setShowAuth(false)} 
        onSuccess={handleAuthSuccess}
        initialMode={authMode}
      />
      {showLearnModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={`${styles.modalHeader} ${styles.infoModalHeader}`}>
              <h3>Moving to Separate Website</h3>
            </div>
            <div className={styles.modalBody}>
              You are about to leave the CodeBox IDE and open the SiliconLogix main website to access the Learning page. Do you wish to proceed?
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowLearnModal(false)}>Cancel</button>
              <button 
                className={styles.confirmDeleteBtn} 
                onClick={handleProceedLearn}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
